import { canPurchaseProduct, getProductSaleStatus, hasProductExposure, showExposurePrice } from "../../../../Component/tool/productExposure";
import { trackLiveEvent } from "../../../../Component/analytics/tracking";
import ProductImageViewer from "./ProductImageViewer";
import UserModalHeader from '../../../../Component/UI/UserModalHeader';
/* eslint-disable react/jsx-pascal-case */
import styled, { keyframes } from "styled-components";
import {  CenterView } from "../../../../Component/style/common_style";
import { useState, useEffect, useLayoutEffect, useRef, useId } from "react";
import { useSaleProductInfo } from "../useSaleProductInfo";

interface Product_Intro_Modal_Props {
  couponAccess?: import("../useLiveCouponProducts").LiveCouponAccess;
  visible: boolean;
  handleModal: React.Dispatch<React.SetStateAction<boolean>>;
  item: any | null;
  refreshVersion?: number;
  isWaiting?: boolean;
  onBuyClick: () => void;
}

const Product_Intro_Modal = ({
  visible,
  handleModal,
  item,
  couponAccess,
  refreshVersion = 0,
  isWaiting = false,
  onBuyClick,
}: Product_Intro_Modal_Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const descriptionId = useId();
  const detailAllowed = !hasProductExposure(item) || showExposurePrice(item?.exposure_price_mode);
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const { images, description, loading, error } = useSaleProductInfo(item?.seq, visible && detailAllowed, refreshVersion);

  // 모달이 닫히거나 열릴 때 인덱스 초기화
  useEffect(() => {
    if (visible) {
      setCurrentIndex(0);
      setExpanded(false);
      setZoomIndex(null);
    }
  }, [visible, item?.seq, images[0]]);

  useEffect(() => {
    if (visible && detailAllowed && item?.seq && item?.live_seq) trackLiveEvent('product_detail', Number(item.live_seq), Number(item.seq));
  }, [visible, detailAllowed, item?.seq, item?.live_seq]);

  useLayoutEffect(() => {
    if (!visible || expanded) return;
    const element = descriptionRef.current;
    if (!element) return;
    const measure = () => setCanExpand(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure); observer.observe(element);
    return () => observer.disconnect();
  }, [visible, expanded, description, loading]);

  if (!item || !detailAllowed) return null;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (images.length < 2) return;
    const diff = touchStart - touchEnd;
    if (diff > 50) {
      // 왼쪽으로 스와이프 (다음 이미지)
      setCurrentIndex((prev) => Math.min(prev + 1, images.length - 1));
    } else if (diff < -50) {
      // 오른쪽으로 스와이프 (이전 이미지)
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setTouchStart(e.clientX);
    setTouchEnd(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setTouchEnd(e.clientX);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (images.length < 2) return;
    const diff = touchStart - touchEnd;
    if (diff > 50) {
      setCurrentIndex((prev) => Math.min(prev + 1, images.length - 1));
    } else if (diff < -50) {
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const closeModal = () => handleModal(false);

  return (
    <BackgroundArea $visible={visible} $isOpen={visible} translate="no"
      onClick={event => { if (event.target === event.currentTarget) closeModal(); }}>
      <ModalBox $isOpen={visible} role="dialog" aria-modal="true" aria-label="상품 소개" onClick={(e: any) => e.stopPropagation()}>
        <UserModalHeader title="상품 소개" onClose={closeModal} />
        <Content_Box>
          {loading ? <p role="status">상품 정보를 불러오고 있습니다.</p>
            : error ? <p role="alert">{error}</p> : <>
          {/* 이미지 캐러셀 슬라이더 */}
          {images.length > 0 ? <SliderContainer
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: isDragging ? "grabbing" : "grab" }}
          >
            <SliderWrapper $currentIndex={currentIndex}>
              {images.map((imgUrl, idx) => (
                <SlideImage
                  key={idx}
                  src={imgUrl}
                  alt={`상품 이미지 ${idx + 1}`}
                  draggable="false"
                  role="button" tabIndex={idx === currentIndex ? 0 : -1} aria-label={`상품 이미지 ${idx + 1} 확대 보기`}
                  onClick={() => { if (Math.abs(touchStart - touchEnd) < 10) setZoomIndex(idx); }}
                  onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setZoomIndex(idx); } }}
                />
              ))}
            </SliderWrapper>
          </SliderContainer> : null}

          {/* 인디케이터 점들 */}
          {images.length > 1 && <PaginationDots>
            {images.map((_, idx) => (
              <Dot key={idx} $active={idx === currentIndex} />
            ))}
          </PaginationDots>}
          <IntroTextContainer>
            <h2>{item.name}</h2>
            <IntroText ref={descriptionRef} $expanded={expanded} id={descriptionId}>
              {description.trim() || "등록된 상품 설명이 없습니다."}
            </IntroText>
            {(canExpand || expanded) && <ExpandDescription type="button" aria-expanded={expanded} aria-controls={descriptionId} onClick={() => setExpanded(value => !value)}>
              {expanded ? '접기' : '펼쳐보기'}<span aria-hidden="true">{expanded ? '⌃' : '⌄'}</span>
            </ExpandDescription>}
          </IntroTextContainer>



          </>}
        </Content_Box>

        {/* 구매하기 버튼 */}
        {!isWaiting && (!couponAccess || couponAccess.canPurchase(Number(item.seq))) && <FooterBuyBtn as="button" disabled={!canPurchaseProduct(item) || item.current_cnt <= 0} onClick={onBuyClick}>
          {item.current_cnt <= 0 || getProductSaleStatus(item.product_status) === '품절' ? '품절' : canPurchaseProduct(item) ? '구매하기' : '판매예정'}
        </FooterBuyBtn>}
      </ModalBox>
      {visible && zoomIndex !== null && <ProductImageViewer images={images} name={item.name} initialIndex={zoomIndex} onClose={() => setZoomIndex(null)} />}
    </BackgroundArea>
  );
};

export default Product_Intro_Modal;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const BackgroundArea = styled.div<{ $visible: boolean; $isOpen: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  background-color: #00000085;
  position: absolute;
  display: ${(props) => (props.$visible ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  transition: opacity 0.5s ease;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  z-index: 99;
`;

const ModalBox = styled.div<{ $isOpen: boolean }>`
  box-sizing: border-box;
  width: 100%;
  max-width: 500px;
  height: 68%; /* 상품소개 전용 높이 축소 */
  background-color: var(--user-surface);
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
  overflow: hidden;
  position: absolute;
  bottom: 0px;
  animation: ${(props) => (props.$isOpen ? slideUp : slideDown)} 0.5s ease;
  color: var(--user-text);
  display: flex;
  flex-direction: column;
`;

const Content_Box = styled.div`
  min-height: 0;
  > p { padding: 0 16px; line-height: 1.5; }
  > p[role="alert"] { color: var(--user-error); }
  width: 100%;
  flex: 1;
  overflow-y: auto;
  padding: 8px 0px 80px 0px; /* 구매하기 버튼 공간 확보 및 패딩 축소 */
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;

  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SliderContainer = styled.div`
  flex-shrink: 0;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  position: relative;
`;

const SliderWrapper = styled.div<{ $currentIndex: number }>`
  display: flex;
  width: 100%;
  height: 100%;
  transform: translateX(-${(props) => props.$currentIndex * 100}%);
  transition: transform 0.3s ease-out;
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  flex-shrink: 0;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  user-select: none;
`;

const PaginationDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  margin-bottom: 16px;
`;

const Dot = styled.div<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => (props.$active ? "var(--user-accent)" : "var(--user-border)")};
  transition: background-color 0.2s ease;
`;

const IntroTextContainer = styled.div`
  flex-shrink: 0;
  h2 { margin: 4px 0 12px; font-size: 18px; line-height: 1.4; color: var(--user-text); }

  width: 100%;
  padding: 12px 16px;
  box-sizing: border-box;
  text-align: left;
`;

const IntroText = styled.div<{ $expanded: boolean }>`
  display: -webkit-box; -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $expanded }) => $expanded ? "unset" : 5};
  overflow: hidden;
  font-size: 16px;
  font-family: var(--font-Pretendard-Bold);
  color: var(--user-text);
  line-height: 24px;
  white-space: pre-wrap;
  word-break: break-all;
`;

const ExpandDescription = styled.button`
  width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
  min-height: 44px; margin-top: 12px; border: 1px solid var(--user-border); border-radius: 8px;
  background: var(--user-surface); color: var(--user-text); font: inherit; cursor: pointer;
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
`;

const FooterBuyBtn = styled(CenterView)`
  border: 0;
  &:disabled { background: var(--user-surface-raised); color: var(--user-text-muted); cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: -4px; }
  background: linear-gradient(135deg, #7955F0, #3209B9);
  color: white;
  font-size: 18px;
  font-family: var(--font-Pretendard-Bold);
  width: 100%;
  height: 60px;
  cursor: pointer;
  position: absolute;
  bottom: 0;
  left: 0;
  user-select: none;
  &:active {
    opacity: 0.9;
  }
`;
